import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { projectRedux } from '../projects/project/ui/state/state';
import {
  IProjectApartmentFilter,
  IPaginated,
  IProjectApartment,
  IProjectSector,
} from '../projects/project/data/entities';
import { IAppReduxState } from '../../redux/store';
import { IAsyncData } from '../../core/models';
import { isPending } from '../../core/redux';
import range from 'lodash/range';
import notFound from './assets/parameter_notfound.png';
import searchImg from './assets/parameter_search.png';

/* =========================================================
   Types
   ========================================================= */
interface IProps {
  projectPlanId: number;
  announcementId?: number;
  sectors?: IProjectSector[];
  /** Name of the currently selected project — rendered as the form title. */
  projectName?: string;
  /** Optional — invoked when a results row is clicked. */
  onApartmentClick?: (apartment: IProjectApartment) => void;
}

interface IInternalState extends IProjectApartmentFilter {
  availableSectorTypes: { sectorType: string }[];
  availableRoomCounts: { roomCount: string; count: string }[];
}

const initialState: IInternalState = {
  availableSectorTypes: [],
  availableRoomCounts: [],
  data: {
    sectorTypes: [],
    /* No default floor range — leave both bounds empty so the
       dropdowns render their "Seçin" placeholder until the user
       picks something. */
    floor: { from: '', to: '' },
    roomCounts: [],
    repairTypes: [],
  },
  projectPlanId: 0,
  page: 1,
  limit: 8,
};

const floorRangeFor = (sectorTypes: { sectorType: string }[]) => {
  if (sectorTypes && sectorTypes.length > 0) {
    const maxFloor = Math.max(
      ...sectorTypes.map((item) => parseInt(item.sectorType, 10)),
    );
    return range(1, maxFloor + 1);
  }
  return [];
};

/* =========================================================
   Icons
   ========================================================= */
const CaretDownIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
    <path
      d="M2 4.5 6 8.5l4-4"
      stroke="#6C757D"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SearchIllustration: React.FC = () => (
  <svg
    width="308"
    height="271"
    viewBox="0 0 308 271"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <g stroke="#BABABA" strokeWidth="1.6" strokeLinecap="round">
      <line x1="79.78" y1="20.45" x2="79.78" y2="31.7" />
      <line x1="74.13" y1="26.08" x2="85.4" y2="26.08" />
      <line x1="278.1" y1="2.56" x2="278.1" y2="13.83" />
      <line x1="272.45" y1="8.21" x2="283.72" y2="8.21" />
      <line x1="39.21" y1="204.71" x2="39.21" y2="215.96" />
      <line x1="33.59" y1="210.33" x2="44.84" y2="210.33" />
    </g>
    <circle cx="154.25" cy="210.85" r="2.56" fill="#CFCFCF" />
    <circle cx="172.4" cy="2.56" r="2.56" fill="#CFCFCF" />
    <rect x="0" y="41.25" width="308.45" height="182.13" rx="6" fill="#F2F2F2" />
    <rect
      x="58.97"
      y="56.55"
      width="202.32"
      height="143.93"
      rx="4"
      fill="#FFFFFF"
      stroke="#D2D2D2"
    />
    <rect x="59.28" y="258.64" width="192.02" height="11.98" rx="2" fill="#F2F2F2" />
    <circle cx="206.05" cy="145.4" r="48.7" fill="#FFFFFF" stroke="#D2D2D2" strokeWidth="3" />
    <rect
      x="244.91"
      y="183.95"
      width="52.39"
      height="14"
      rx="6"
      transform="rotate(43 244.91 183.95)"
      fill="#D2D2D2"
    />
    <circle cx="247.18" cy="187.18" r="6.5" fill="#BABABA" />
  </svg>
);

/* =========================================================
   Field components
   ========================================================= */
type Option = { label: string; value: string };

interface SelectFieldProps {
  value: string;
  options: Option[];
  placeholder?: string;
  onChange: (value: string) => void;
}

/** A click-to-toggle dropdown that matches the Figma "Searchbox" field. */
const SelectField: React.FC<SelectFieldProps> = ({
  value,
  options,
  placeholder = 'Seçin',
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div className={`asp-select ${open ? 'asp-select--open' : ''}`} ref={ref}>
      <button
        type="button"
        className="asp-select__trigger"
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className={`asp-select__value ${
            !selected ? 'asp-select__value--placeholder' : ''
          }`}
        >
          {selected ? selected.label : placeholder}
        </span>
        <CaretDownIcon />
      </button>
      {open && options.length > 0 && (
        <ul className="asp-select__menu" role="listbox">
          {options.map((opt) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              className={`asp-select__option ${
                opt.value === value ? 'asp-select__option--active' : ''
              }`}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

interface MultiSelectFieldProps {
  values: string[];
  options: Option[];
  placeholder?: string;
  onChange: (values: string[]) => void;
}

/** Multi-select variant — same trigger styling as SelectField but each
 *  option toggles its presence in `values`. The trigger shows the chosen
 *  labels comma-separated, the menu keeps an open state across picks. */
const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  values,
  options,
  placeholder = 'Seçin',
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selectedLabels = options
    .filter((o) => values.includes(o.value))
    .map((o) => o.label);

  function toggle(val: string) {
    if (values.includes(val)) {
      onChange(values.filter((v) => v !== val));
    } else {
      onChange([...values, val]);
    }
  }

  return (
    <div className={`asp-select ${open ? 'asp-select--open' : ''}`} ref={ref}>
      <button
        type="button"
        className="asp-select__trigger"
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className={`asp-select__value ${
            selectedLabels.length === 0 ? 'asp-select__value--placeholder' : ''
          }`}
        >
          {selectedLabels.length === 0 ? placeholder : selectedLabels.join(', ')}
        </span>
        <CaretDownIcon />
      </button>
      {open && options.length > 0 && (
        <ul className="asp-select__menu" role="listbox" aria-multiselectable="true">
          {options.map((opt) => {
            const checked = values.includes(opt.value);
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={checked}
                className={`asp-select__option asp-select__option--multi ${
                  checked ? 'asp-select__option--active' : ''
                }`}
                onClick={() => toggle(opt.value)}
              >
                <span
                  className={`asp-select__checkbox ${
                    checked ? 'asp-select__checkbox--checked' : ''
                  }`}
                  aria-hidden
                >
                  {checked && (
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path
                        d="M2 5.2 4.2 7.4 8 3"
                        stroke="#fff"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                {opt.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/* =========================================================
   Pagination
   ========================================================= */
interface PaginationProps {
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
}

/** Builds a compact page list — first, current ± 1, last — with
 *  ellipses for the gaps. Returns a mixed array of numbers and '…'
 *  tokens that the renderer pages-or-spacers from. */
function buildPageWindow(page: number, pageCount: number): (number | '…')[] {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  const window: (number | '…')[] = [];
  const start = Math.max(2, page - 1);
  const end = Math.min(pageCount - 1, page + 1);
  window.push(1);
  if (start > 2) window.push('…');
  for (let p = start; p <= end; p++) window.push(p);
  if (end < pageCount - 1) window.push('…');
  window.push(pageCount);
  return window;
}

const Pagination: React.FC<PaginationProps> = ({ page, pageCount, onChange }) => {
  if (pageCount <= 1) return null;
  const items = buildPageWindow(page, pageCount);

  return (
    <nav className="asp-pagination" aria-label="Səhifələr">
      <button
        type="button"
        className="asp-pagination__nav"
        disabled={page === 1}
        onClick={() => onChange(page - 1)}
        aria-label="Əvvəlki"
      >
        ‹
      </button>
      {items.map((it, i) =>
        it === '…' ? (
          <span key={`gap-${i}`} className="asp-pagination__gap">…</span>
        ) : (
          <button
            key={it}
            type="button"
            className={`asp-pagination__page${
              it === page ? ' asp-pagination__page--active' : ''
            }`}
            onClick={() => onChange(it)}
          >
            {it}
          </button>
        ),
      )}
      <button
        type="button"
        className="asp-pagination__nav"
        disabled={page === pageCount}
        onClick={() => onChange(page + 1)}
        aria-label="Növbəti"
      >
        ›
      </button>
    </nav>
  );
};

/* =========================================================
   Main panel
   ========================================================= */
export const ApartmentSearchPanel: React.FC<IProps> = ({
  projectPlanId,
  announcementId,
  sectors,
  projectName,
  onApartmentClick,
}) => {
  const dispatch = useDispatch();
  const [state, setState] = useState<IInternalState>(initialState);
  const [hasSearched, setHasSearched] = useState(false);
  /* Per-field "required and empty" flags — set on submit when a field
     is blank, cleared as soon as the user fills it. All three filters
     must be chosen before a search runs. */
  const [errors, setErrors] = useState({
    sectorTypes: false,
    floor: false,
    roomCounts: false,
  });

  const apartmentsBranch = useSelector<
    IAppReduxState,
    IAsyncData<IPaginated<IProjectApartment>>
  >((s) => s.project.apartments);

  /* Sync projectPlanId + announcementId from props and reset every
     filter back to its placeholder state. Mirrors how
     ApartmentSearchModal.tsx scopes per announcement — and matches
     the address / map panels which clear their selections when the
     user picks a different project card above. */
  useEffect(() => {
    setState((prev) => ({
      ...prev,
      projectPlanId,
      announcementId,
      data: { ...initialState.data },
      page: 1,
    }));
    setHasSearched(false);
  }, [projectPlanId, announcementId]);

  /* Build dropdown option lists from incoming sectors data. */
  useEffect(() => {
    if (!sectors) return;
    setState((prev) => {
      const uniqueBuildingTypes = Array.from(
        new Set(sectors.map((s) => s.buildingType)),
      ).sort((a, b) => a - b);

      const totals = sectors.reduce(
        (acc, s) => {
          acc.studioCount += s.studioCount;
          acc.oneRoomCount += s.oneRoomCount;
          acc.twoRoomCount += s.twoRoomCount;
          acc.threeRoomCount += s.threeRoomCount;
          acc.fourRoomCount += s.fourRoomCount;
          return acc;
        },
        {
          studioCount: 0,
          oneRoomCount: 0,
          twoRoomCount: 0,
          threeRoomCount: 0,
          fourRoomCount: 0,
        },
      );

      return {
        ...prev,
        data: {
          ...prev.data,
          /* Don't seed floor.from / floor.to here — leave whatever
             the user picked (or the empty initial state) so the
             "Seçin" placeholder shows until they make a selection. */
        },
        availableSectorTypes: uniqueBuildingTypes.map((t) => ({
          sectorType: String(t),
        })),
        availableRoomCounts: [
          { roomCount: '0', count: String(totals.studioCount) },
          { roomCount: '1', count: String(totals.oneRoomCount) },
          { roomCount: '2', count: String(totals.twoRoomCount) },
          { roomCount: '3', count: String(totals.threeRoomCount) },
          { roomCount: '4', count: String(totals.fourRoomCount) },
        ],
      };
    });
    setHasSearched(false);
  }, [sectors]);

  /* Selected values mirror the array shape used by the API. */
  const selectedSectorTypes = state.data.sectorTypes.map((s) => s.sectorType);
  const selectedRoomCounts = state.data.roomCounts.map((r) => r.roomCount);

  /* Option lists for the dropdowns. */
  const sectorOptions = useMemo<Option[]>(
    () =>
      state.availableSectorTypes.map((s) => ({
        value: s.sectorType,
        label: `${s.sectorType} mərtəbəli`,
      })),
    [state.availableSectorTypes],
  );

  const floorOptions = useMemo<Option[]>(
    () =>
      floorRangeFor(state.availableSectorTypes).map((f) => ({
        value: String(f),
        label: String(f),
      })),
    [state.availableSectorTypes],
  );

  const roomOptions = useMemo<Option[]>(
    () =>
      state.availableRoomCounts
        .filter((r) => +r.count > 0)
        .map((r) => ({
          value: r.roomCount,
          label: r.roomCount === '0' ? 'Studio tipli' : `${r.roomCount} otaqlı`,
        })),
    [state.availableRoomCounts],
  );

  /* Handlers. */
  function setSectorTypes(values: string[]) {
    if (values.length > 0) setErrors((e) => ({ ...e, sectorTypes: false }));
    setState((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        sectorTypes: values.map((sectorType) => ({ sectorType })),
      },
    }));
  }

  function setRoomCounts(values: string[]) {
    if (values.length > 0) setErrors((e) => ({ ...e, roomCounts: false }));
    setState((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        roomCounts: values.map((roomCount) => ({ roomCount })),
      },
    }));
  }

  function setFloorFrom(value: string) {
    if (value && state.data.floor.to) setErrors((e) => ({ ...e, floor: false }));
    setState((prev) => ({
      ...prev,
      data: { ...prev.data, floor: { ...prev.data.floor, from: value } },
    }));
  }

  function setFloorTo(value: string) {
    if (value && state.data.floor.from) setErrors((e) => ({ ...e, floor: false }));
    setState((prev) => ({
      ...prev,
      data: { ...prev.data, floor: { ...prev.data.floor, to: value } },
    }));
  }

  function reset() {
    /* Reset clears only the FORM inputs. The results list (Redux
       `s.project.apartments`) plus `hasSearched`/`page` are left
       untouched, so the previously-searched table and its pagination
       stay on screen until the user runs a new search. */
    setState((prev) => ({
      ...prev,
      /* Clear every filter back to the placeholder state, including
         the floor range — no default 1..maxFloor seeding. */
      data: { ...initialState.data },
    }));
    setErrors({ sectorTypes: false, floor: false, roomCounts: false });
  }

  function search() {
    /* All three filters are required before a search runs. */
    const nextErrors = {
      sectorTypes: state.data.sectorTypes.length === 0,
      floor: !state.data.floor.from || !state.data.floor.to,
      roomCounts: state.data.roomCounts.length === 0,
    };
    setErrors(nextErrors);
    if (nextErrors.sectorTypes || nextErrors.floor || nextErrors.roomCounts) {
      return;
    }
    const next = { ...state, page: 1 };
    setState(next);
    setHasSearched(true);
    dispatch(projectRedux.actions.searchApartments(next));
  }

  function goToPage(page: number) {
    const next = { ...state, page };
    setState(next);
    dispatch(projectRedux.actions.searchApartments(next));
  }

  /* Render-state computation. */
  const isLoadingResults = isPending(apartmentsBranch);
  const apartments = apartmentsBranch.data?.list ?? [];
  const showResults = hasSearched && !isLoadingResults && apartments.length > 0;
  const showNoResults = hasSearched && !isLoadingResults && apartments.length === 0;
  const showInitial = !hasSearched && !isLoadingResults;

  return (
    <div className="asp">
      <aside className="asp__form">
        <h3 className="asp__form-title">{projectName ?? ''}</h3>

        <div className="asp__fields">
          <div className="asp-field">
            <label className="asp-field__label">Bina tipi</label>
            <MultiSelectField
              values={selectedSectorTypes}
              options={sectorOptions}
              onChange={setSectorTypes}
            />
            {errors.sectorTypes && (
              <span className="asp-field__error">Bina tipini seçin.</span>
            )}
          </div>

          <div className="asp-field">
            <label className="asp-field__label">Mərtəbə seçimi</label>
            <div className="asp-field__row">
              <SelectField
                value={state.data.floor.from}
                options={floorOptions}
                onChange={setFloorFrom}
              />
              <SelectField
                value={state.data.floor.to}
                options={floorOptions}
                onChange={setFloorTo}
              />
            </div>
            {errors.floor && (
              <span className="asp-field__error">Mərtəbə aralığını seçin.</span>
            )}
          </div>

          <div className="asp-field">
            <label className="asp-field__label">Otaq sayı</label>
            <MultiSelectField
              values={selectedRoomCounts}
              options={roomOptions}
              onChange={setRoomCounts}
            />
            {errors.roomCounts && (
              <span className="asp-field__error">Otaq sayını seçin.</span>
            )}
          </div>
        </div>

        <div className="asp__actions">
          <button type="button" className="asp-btn asp-btn--ghost" onClick={reset}>
           Yenilə
          </button>
          <button type="button" className="asp-btn asp-btn--primary" onClick={search}>
            Axtar
          </button>
        </div>
      </aside>

      <div className={`asp__panel${showResults ? ' asp__panel--results' : ''}`}>
        {isLoadingResults && (
          <div className="asp__loading">
            <div className="asp__spinner" />
          </div>
        )}

        {showInitial && (
          <div className="asp__empty">
            <img src={searchImg} />
            <p className="asp__empty-text">Parametrlər üzrə axtarış edin.</p>
          </div>
        )}

        {showNoResults && (
          <div className="asp__empty">
            <img src={notFound}/>
            <p className="asp__empty-text">Parametrlərə uyğun nəticə tapılmadı.</p>
          </div>
        )}

        {showResults && (
          <div className="asp__results">
            <div className="asp-table-wrap">
              <table className="asp-table">
                <thead>
                  <tr>
                    <th>Bina</th>
                    <th>Giriş</th>
                    <th>Mərtəbə</th>
                    <th>Mənzil</th>
                    <th>Otaq sayı</th>
                    <th>Sahə</th>
                    <th>Qiymət</th>
                  </tr>
                </thead>
                <tbody>
                  {apartments.map((apt) => (
                    <tr key={apt.id} onClick={() => onApartmentClick?.(apt)}>
                      <td>{apt.buildingNumber}</td>
                      <td>{apt.entrance}</td>
                      <td>{apt.floorNumber}</td>
                      <td>{apt.apartmentNumber}</td>
                      <td>{apt.roomCount === '0' ? 'Studio' : apt.roomCount}</td>
                      <td>{apt.internalSize} m²</td>
                      <td>{apt.price} AZN</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="asp__results-meta">
              <span>Cəmi: {apartmentsBranch.data?.total ?? 0}</span>
              <Pagination
                page={state.page}
                pageCount={apartmentsBranch.data?.pageCount ?? 0}
                onChange={goToPage}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApartmentSearchPanel;
